import React from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  LinearProgress,
  Skeleton,
  Stack,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import DownloadOutlined from "@mui/icons-material/DownloadOutlined";
import RefreshOutlined from "@mui/icons-material/RefreshOutlined";
import ShareOutlined from "@mui/icons-material/ShareOutlined";
import PlayCircleOutlineOutlined from "@mui/icons-material/PlayCircleOutlineOutlined";
import PlaylistAddOutlined from "@mui/icons-material/PlaylistAddOutlined";
import CheckOutlined from "@mui/icons-material/CheckOutlined";
import VolumeOffOutlined from "@mui/icons-material/VolumeOffOutlined";
import CircularProgress from "@mui/material/CircularProgress";
import { modelSupportsNativeAudio } from "../modelProfiles";
import {
  downloadVideoStudioJob,
  videoJobDownloadFilename,
} from "../api/downloadVideoStudioJob";
import {
  GENERATION_MOTIVATION_QUOTES,
  nextMotivationIndex,
} from "../generationMotivationQuotes";

const STATUS_COLOR = {
  idle: "default",
  queued: "info",
  generating: "warning",
  stitching: "info",
  voiceover: "info",
  done: "success",
  failed: "error",
};

const MOTIVATION_ROTATE_MS = 5000;

function GenerationMotivationLine({ active }) {
  const [index, setIndex] = React.useState(() =>
    Math.floor(Math.random() * GENERATION_MOTIVATION_QUOTES.length),
  );

  React.useEffect(() => {
    if (!active) return undefined;
    setIndex(Math.floor(Math.random() * GENERATION_MOTIVATION_QUOTES.length));
    const id = window.setInterval(() => {
      setIndex((prev) => nextMotivationIndex(prev));
    }, MOTIVATION_ROTATE_MS);
    return () => window.clearInterval(id);
  }, [active]);

  if (!active) return null;

  return (
    <Typography
      variant="caption"
      sx={{
        color: alpha("#fff", 0.72),
        textAlign: "center",
        maxWidth: 320,
        px: 1,
        lineHeight: 1.45,
        fontStyle: "italic",
        transition: "opacity 0.35s ease",
      }}
    >
      {GENERATION_MOTIVATION_QUOTES[index]}
    </Typography>
  );
}

export default function OutputPanel({
  status,
  statusLabel,
  segmentProgress: _segmentProgress,
  taskId,
  jobId,
  videoUrl,
  jobTitle,
  modelId,
  onRegenerate,
  onShare,
  onAddToStitch,
  inStitchQueue,
  lang,
}) {
  const theme = useTheme();
  const videoRef = React.useRef(null);
  const isBusy =
    status === "queued" || status === "generating" || status === "stitching" || status === "voiceover";
  const isDone = status === "done" && Boolean(videoUrl);
  const isFailed = status === "failed";
  const silentOutput = isDone && modelId && !modelSupportsNativeAudio(modelId);

  const [elapsed, setElapsed] = React.useState(0);
  const [downloading, setDownloading] = React.useState(false);
  const [downloadProgress, setDownloadProgress] = React.useState(null);
  const startedAtRef = React.useRef(null);
  React.useEffect(() => {
    if (!isBusy) {
      startedAtRef.current = null;
      setElapsed(0);
      return undefined;
    }
    // Restart the clock whenever a new job becomes busy (don't accumulate across stuck polls).
    startedAtRef.current = Date.now();
    setElapsed(0);
    const id = setInterval(() => {
      setElapsed(Math.floor((Date.now() - startedAtRef.current) / 1000));
    }, 1000);
    return () => clearInterval(id);
  }, [isBusy, jobId]);

  React.useEffect(() => {
    const el = videoRef.current;
    if (!el || !isDone) return;
    const onMeta = () => {
      if (el.videoWidth && el.videoHeight) {
        el.dataset.aspectRatio = `${el.videoWidth}:${el.videoHeight}`;
      }
    };
    el.addEventListener("loadedmetadata", onMeta);
    return () => el.removeEventListener("loadedmetadata", onMeta);
  }, [videoUrl, isDone]);

  const handleDownload = async () => {
    if (!isDone || downloading) return;
    if (!jobId) {
      window.alert(
        lang?.videoStudioDownloadNoJob ||
          "Save this video to your library first, then download from Video Library.",
      );
      return;
    }
    setDownloading(true);
    setDownloadProgress({ phase: "preparing", percent: 0 });
    try {
      await downloadVideoStudioJob(
        jobId,
        videoJobDownloadFilename({ id: jobId, title: jobTitle }),
        { onProgress: (info) => setDownloadProgress(info) },
      );
    } catch (err) {
      window.alert(
        err?.message || lang?.videoStudioDownloadFailed || "Download failed. Try again.",
      );
    } finally {
      setDownloading(false);
      setDownloadProgress(null);
    }
  };

  const downloadLabel = (() => {
    if (!downloading) return lang?.videoStudioDownload || "Download";
    const phase = downloadProgress?.phase;
    const pct = downloadProgress?.percent;
    if (phase === "preparing") return lang?.videoStudioDownloadPreparing || "Preparing…";
    if (phase === "saving") return lang?.videoStudioDownloadSaving || "Saving…";
    if (typeof pct === "number") {
      return `${lang?.videoStudioDownloading || "Downloading"} ${pct}%`;
    }
    return lang?.videoStudioDownloading || "Downloading…";
  })();

  return (
    <Card
      variant="outlined"
      data-tour="video-studio-output"
      sx={{
        borderRadius: 2,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        border: `1px solid ${theme.palette.divider}`,
      }}
    >
      <CardContent sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 2 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
          <Typography variant="subtitle1" fontWeight={800}>
            {lang?.videoStudioOutput || "Output"}
          </Typography>
          {silentOutput ? (
            <Chip
              size="small"
              icon={<VolumeOffOutlined sx={{ fontSize: "16px !important" }} />}
              label={lang?.videoStudioSilentOutput || "Silent video (no audio)"}
              color="warning"
              variant="outlined"
              sx={{ fontWeight: 700 }}
            />
          ) : null}
        </Stack>

        <Box
          sx={{
            position: "relative",
            pt: "56.25%",
            borderRadius: 2,
            overflow: "hidden",
            bgcolor: alpha(theme.palette.common.black, 0.85),
            border: `1px solid ${theme.palette.divider}`,
          }}
        >
          {isBusy && (
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 2,
                p: 2,
              }}
            >
              <Skeleton variant="rectangular" width="100%" height="100%" sx={{ position: "absolute", inset: 0 }} />
              <LinearProgress sx={{ width: "70%", zIndex: 1 }} />
              <Stack alignItems="center" spacing={0.5} sx={{ zIndex: 1 }}>
                <Typography variant="caption" color="common.white" sx={{ fontWeight: 600 }}>
                  {statusLabel}
                  {elapsed > 0 && ` · ${elapsed}s`}
                </Typography>
                <GenerationMotivationLine active={isBusy} />
                {taskId && (
                  <Typography
                    variant="caption"
                    sx={{
                      fontFamily: "monospace",
                      fontSize: "0.62rem",
                      color: alpha("#fff", 0.5),
                      wordBreak: "break-all",
                      textAlign: "center",
                    }}
                  >
                    {taskId}
                  </Typography>
                )}
              </Stack>
            </Box>
          )}
          {!isBusy && !isDone && !isFailed && (
            <Stack
              alignItems="center"
              justifyContent="center"
              sx={{ position: "absolute", inset: 0, color: alpha("#fff", 0.5) }}
            >
              <PlayCircleOutlineOutlined sx={{ fontSize: 56 }} />
              <Typography variant="caption" sx={{ mt: 1 }}>
                {lang?.videoStudioPreviewPlaceholder || "Your video will appear here"}
              </Typography>
            </Stack>
          )}
          {isFailed && (
            <Stack
              alignItems="center"
              justifyContent="center"
              sx={{ position: "absolute", inset: 0, color: alpha("#fff", 0.7), p: 2 }}
            >
              <Typography variant="caption" textAlign="center" fontWeight={600}>
                {lang?.videoStudioStatusFailed || "Generation failed"}
              </Typography>
            </Stack>
          )}
          {isDone && videoUrl && (
            <Box
              component="video"
              ref={videoRef}
              src={videoUrl}
              controls
              playsInline
              sx={{
                position: "absolute",
                inset: 0,
                width: "100%",
                height: "100%",
                objectFit: "contain",
                bgcolor: "#000",
              }}
            />
          )}
        </Box>

        <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
          <Typography variant="caption" color="text.secondary" fontWeight={600}>
            {lang?.videoStudioTaskStatus || "Task status"}
          </Typography>
          <Chip size="small" label={statusLabel} color={STATUS_COLOR[status] || "default"} sx={{ fontWeight: 700 }} />
        </Stack>

        {taskId && (
          <Typography variant="caption" color="text.secondary" sx={{ fontFamily: "monospace", wordBreak: "break-all" }}>
            ID: {taskId}
          </Typography>
        )}

        <Stack spacing={1} mt="auto">
          {onAddToStitch ? (
            <Button
              variant={inStitchQueue ? "outlined" : "contained"}
              color={inStitchQueue ? "success" : "secondary"}
              fullWidth
              startIcon={inStitchQueue ? <CheckOutlined /> : <PlaylistAddOutlined />}
              disabled={!isDone}
              onClick={onAddToStitch}
            >
              {inStitchQueue
                ? lang?.videoStitchInQueue || "In Clip board"
                : lang?.videoStitchAddClip || "Add to Clip board"}
            </Button>
          ) : null}
          <Button
            variant="contained"
            fullWidth
            startIcon={
              downloading ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <DownloadOutlined />
              )
            }
            disabled={!isDone || downloading}
            onClick={handleDownload}
          >
            {downloadLabel}
          </Button>
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              fullWidth
              startIcon={<RefreshOutlined />}
              onClick={onRegenerate}
              disabled={isBusy}
            >
              {lang?.videoStudioRegenerate || "Regenerate"}
            </Button>
            <Button
              variant="outlined"
              fullWidth
              startIcon={<ShareOutlined />}
              onClick={onShare}
              disabled={!isDone}
            >
              {lang?.videoStudioShare || "Share"}
            </Button>
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
}
